import type { ReactNode } from 'react';
import { Link } from 'react-router-dom';
import type { LucideIcon } from 'lucide-react';
import { Check, ChevronsUpDown } from 'lucide-react';
import { cn } from '@reflex/ui/lib/utils';
import { PageTitle, PageDescription } from '@reflex/ui/components/layout/Typography';
import { useRegisterPageChrome } from '@/components/layout/TakeoverShell/page-chrome';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from '@reflex/ui/components/ui/dropdown-menu';

/**
 * Left-rail hub chrome (Agents, Organization, MCP, Credentials). The hub
 * owns the page title on desktop; on mobile the takeover header already
 * names the hub, so the in-page `PageTitle` is hidden and the subtitle
 * moves behind the chrome info icon. Embedded section pages should omit
 * their `PageHeader` title and keep description / docs / actions only —
 * see `PageHeader` with no `title`.
 */

export interface HubSection {
  id: string;
  label: string;
  icon: LucideIcon;
  description: string;
  /**
   * Read permission for the section body. The hub rail always shows the row;
   * wrap the body in `HubReadGuard` so missing read renders `NoReadAccessState`.
   */
  permission?: string;
}

export interface HubScopeToggleProps {
  available: readonly string[];
  active: string;
  labels: Record<string, string>;
  onChange: (scope: string) => void;
}

export interface HubLayoutProps {
  title: string;
  description: string;
  sections: readonly HubSection[];
  activeSectionId: string;
  sectionHref: (section: HubSection) => string;
  navLabel: string;
  scopes?: HubScopeToggleProps;
  headerSlot?: ReactNode;
  children: ReactNode;
}

export function HubScopeToggle({ available, active, labels, onChange }: HubScopeToggleProps) {
  return (
    <div className="inline-flex rounded-lg border border-border bg-card p-0.5">
      {available.map((scope) => (
        <button
          key={scope}
          type="button"
          onClick={() => onChange(scope)}
          className={cn(
            'rounded-md px-3.5 py-1.5 text-sm font-medium transition-colors',
            active === scope
              ? 'bg-secondary text-foreground'
              : 'text-muted-foreground hover:text-foreground',
          )}
        >
          {labels[scope] ?? scope}
        </button>
      ))}
    </div>
  );
}

function HubSectionSelect({
  sections,
  activeSectionId,
  sectionHref,
}: {
  sections: readonly HubSection[];
  activeSectionId: string;
  sectionHref: (section: HubSection) => string;
}) {
  const active = sections.find((section) => section.id === activeSectionId) ?? sections[0];
  if (!active) return null;
  const ActiveIcon = active.icon;

  return (
    <DropdownMenu>
      <DropdownMenuTrigger asChild>
        <button
          type="button"
          className="flex w-full items-center gap-2.5 rounded-md border border-border bg-card px-3 py-2 text-left text-sm md:hidden"
          data-testid="hub-section-select"
          aria-label="Select section"
        >
          <ActiveIcon className="size-4 shrink-0 text-muted-foreground" />
          <span className="min-w-0 flex-1 truncate font-medium">{active.label}</span>
          <ChevronsUpDown className="size-3.5 shrink-0 text-muted-foreground/60" />
        </button>
      </DropdownMenuTrigger>
      <DropdownMenuContent align="start" className="w-[var(--radix-dropdown-menu-trigger-width)]">
        {sections.map((item) => {
          const selected = item.id === activeSectionId;
          return (
            <DropdownMenuItem key={item.id} asChild>
              <Link
                to={sectionHref(item)}
                className="flex items-center gap-2.5"
                data-testid={`hub-section-${item.id}`}
              >
                <item.icon className="size-4 shrink-0" />
                <span className="min-w-0 flex-1 truncate">{item.label}</span>
                {selected && <Check className="size-3.5 shrink-0 text-muted-foreground" />}
              </Link>
            </DropdownMenuItem>
          );
        })}
      </DropdownMenuContent>
    </DropdownMenu>
  );
}

export function HubLayout({
  title,
  description,
  sections,
  activeSectionId,
  sectionHref,
  navLabel,
  scopes,
  headerSlot,
  children,
}: HubLayoutProps) {
  const chromeActive = useRegisterPageChrome({ title, description });

  return (
    <div
      className={cn(
        'mx-auto flex h-full w-full max-w-5xl flex-col px-4 pb-6 pt-3 md:px-6 md:py-8',
        // Takeover chrome already names the hub; do not keep intro margin
        // after the title and subtitle are hidden.
        chromeActive && 'max-md:pt-2',
      )}
    >
      <div data-testid="hub-intro" className={cn('mb-4 md:mb-6', chromeActive && 'max-md:hidden')}>
        <PageTitle className="hidden md:block">{title}</PageTitle>
        <PageDescription className={cn('mt-0 md:mt-1', chromeActive && 'max-md:hidden')}>
          {description}
        </PageDescription>
      </div>

      {(scopes || headerSlot) && (
        <div className="mb-6 flex flex-wrap items-center gap-3">
          {scopes && (
            <HubScopeToggle
              available={scopes.available}
              active={scopes.active}
              labels={scopes.labels}
              onChange={scopes.onChange}
            />
          )}
          {headerSlot}
        </div>
      )}

      <div className="flex min-h-0 flex-1 flex-col gap-6 md:flex-row">
        <nav className="md:w-52 md:shrink-0" aria-label={navLabel}>
          <HubSectionSelect
            sections={sections}
            activeSectionId={activeSectionId}
            sectionHref={sectionHref}
          />
          <ul className="hidden flex-col md:flex">
            {sections.map((item) => (
              <li key={item.id}>
                <Link
                  to={sectionHref(item)}
                  className={cn(
                    'flex w-full items-center gap-2.5 whitespace-nowrap rounded-md px-2.5 py-2 text-left text-sm transition-colors',
                    item.id === activeSectionId
                      ? 'bg-secondary font-medium text-foreground'
                      : 'text-muted-foreground hover:bg-accent hover:text-foreground',
                  )}
                >
                  <item.icon className="size-4 shrink-0" />
                  <span>{item.label}</span>
                </Link>
              </li>
            ))}
          </ul>
        </nav>

        <div className="min-w-0 flex-1 overflow-y-auto">{children}</div>
      </div>
    </div>
  );
}
